import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  Client,
  Invoice,
  Organization,
  OrganizationListItem,
} from '@repo/api-client/dtos';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { TemplateCanvas } from '@/features/preview/TemplateCanvas';
import { buildArtboardPayload } from '@/features/preview/buildArtboardPayload';
import { CustomizeToolbar } from '@/features/preview/CustomizeToolbar';
import { useCustomize } from '@/features/preview/useCustomize';

interface InvoicePreviewDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Synthesised wire-shape invoice (typically from
   * `buildPreviewInvoice`). The dialog re-renders whenever this
   * reference changes, so a parent that memoises by form values gets
   * a live preview as the user types.
   */
  invoice: Invoice;
  organization: Organization | OrganizationListItem | null;
  client: Client | null;
}

/**
 * Modal preview surface for the in-flight invoice. The
 * `TemplateCanvas` re-renders on every payload change, so any edit
 * the user has made up to the moment they tap Preview shows up here
 * immediately. The floating `CustomizeToolbar` overlays the canvas
 * so the user can flip template / accent / options without leaving
 * the form — same affordance as mobile's preview screen.
 */
export function InvoicePreviewDialog({
  open,
  onOpenChange,
  invoice,
  organization,
  client,
}: InvoicePreviewDialogProps) {
  // Measure the canvas slot so the artboard scales smoothly on
  // window resize, same trick as the dedicated /preview route.
  const hostRef = useRef<HTMLDivElement>(null);
  const [hostWidth, setHostWidth] = useState<number>(794);
  useLayoutEffect(() => {
    if (!open) return;
    const node = hostRef.current;
    if (!node) return;
    const update = () => {
      const w = node.clientWidth;
      if (w > 0) setHostWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [open]);

  const customize = useCustomize({ organization });

  const payload = useMemo(() => {
    if (!customize.effectiveOrg) return null;
    return buildArtboardPayload({
      invoice,
      organization: widenOrganization(customize.effectiveOrg),
      client,
      // The customize toolbar drives `effectiveTemplateId`. Fall back
      // to the invoice's own templateId only if the user hasn't picked
      // one in the toolbar (i.e. the org's saved branding wins).
      templateId: customize.effectiveTemplateId,
    });
  }, [invoice, customize.effectiveOrg, customize.effectiveTemplateId, client]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Width tuned to hug the A4 canvas (794 px) + small side
          gutter; previously max-w-[960px] left ~80 px of dead
          space on each side because the artboard never scales
          past its natural 1× width. Height is generous so multi-
          page invoices scroll inside the dialog, not the page. */}
      <DialogContent className="flex h-[90vh] w-[92vw] max-w-[860px] flex-col gap-0 overflow-hidden p-0">
        <div className="flex items-center justify-between gap-3 border-b border-line bg-card px-4 py-2.5">
          <DialogTitle className="text-sm font-semibold">Preview</DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Live · updates as you type
          </p>
        </div>
        <div
          ref={hostRef}
          className="flex flex-1 items-start justify-center overflow-auto bg-secondary/40 px-3 py-4 pb-16"
        >
          {payload ? (
            <TemplateCanvas
              payload={payload}
              width={Math.min(hostWidth - 24, 794)}
              templateId={customize.effectiveTemplateId}
              className="rounded-md shadow-lg shadow-black/10 ring-1 ring-black/5"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Loading workspace…
            </p>
          )}
        </div>

        {/* Toolbar sits OUTSIDE the scroll container, positioned
            against the DialogContent (which is `position: fixed` and
            therefore the containing block for `absolute`). This keeps
            the pills pinned to the visible bottom of the dialog
            regardless of how far the canvas has been scrolled. */}
        {organization ? (
          <CustomizeToolbar
            templateId={customize.effectiveTemplateId}
            onPickTemplate={customize.setTemplate}
            accent={customize.effectiveAccent}
            onPickAccent={customize.setAccent}
            showLogo={customize.effectiveShowLogo}
            onToggleShowLogo={customize.setShowLogo}
            showFooter={customize.effectiveShowFooter}
            onToggleShowFooter={customize.setShowFooter}
            showItemDiscount={customize.effectiveShowItemDiscount}
            onToggleShowItemDiscount={customize.setShowItemDiscount}
            showItemTax={customize.effectiveShowItemTax}
            onToggleShowItemTax={customize.setShowItemTax}
            showItemNotes={customize.effectiveShowItemNotes}
            onToggleShowItemNotes={customize.setShowItemNotes}
            saving={customize.saving}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * `useActiveOrganization` returns the LIST shape (no `taxNumber`,
 * `businessNumber`, `registrationNumber`). The artboard's templates
 * read those with optional chaining and `?? null`, so widening with
 * nulls keeps the live preview consistent with the saved-invoice
 * preview. The printer still uses the full org for the real PDF.
 */
function widenOrganization(
  org: Organization | OrganizationListItem,
): Organization {
  const { role: _role, stats: _stats, ...core } = org as OrganizationListItem & {
    businessNumber?: string | null;
    taxNumber?: string | null;
    registrationNumber?: string | null;
  };
  return {
    businessNumber: null,
    taxNumber: null,
    registrationNumber: null,
    ...core,
  } as Organization;
}
